import { PolicyAutoscalerMode, WorkloadScalingPolicyConfig } from '@cast/types';

import {
  autoscalerModeDisplayName,
  getLookbackDaysHours,
} from '@cast/split-me/features/cluster/workload-optimization';

import type { DisplayPolicy, MetricsGridItem, WorkloadRow } from './_types';
import type {
  AutopilotRecommendation,
  WrappedPolicy,
} from '../../../open-api-types/autopilot-types';

export const formatQuantile = (
  config?: WorkloadScalingPolicyConfig
): string => {
  if (!config) {
    return '—';
  }

  if (config.function === 'MAX') {
    return 'Max';
  }

  if (config.function === 'QUANTILE' && config.args?.[0]) {
    const value = Number(config.args[0]);

    if (!Number.isNaN(value)) {
      return `p${Math.round(value * 100)}`;
    }
  }

  return config.function ?? '—';
};

export const formatOverhead = (overhead?: number): string => {
  if (overhead == null) {
    return '—';
  }

  return `${Math.round(overhead * 100)}%`;
};

export const formatApplyThreshold = (threshold?: number): string => {
  if (threshold == null) {
    return '—';
  }

  return `${Math.round(threshold * 100)}%`;
};

export const formatLookbackWindow = (seconds?: number | null): string => {
  if (seconds == null || seconds === 0) {
    return '—';
  }

  const { days, hours } = getLookbackDaysHours({
    lookBackPeriodSeconds: seconds,
  });

  if (days > 0 && hours === 0) {
    return days === 1 ? '1 day' : `${days} days`;
  }

  if (days === 0 && hours > 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }

  return `${days}d ${hours}h`;
};

export const getMetricsGridItems = (
  policy: WrappedPolicy
): MetricsGridItem[] => {
  const { recommendationPolicies, applyType } = policy.payload;
  const cpu = recommendationPolicies?.cpu;
  const memory = recommendationPolicies?.memory;
  const resolvedApplyType =
    applyType ??
    recommendationPolicies?.applyType ??
    PolicyAutoscalerMode.UNKNOWN;

  return [
    { label: 'CPU QUANTILE', value: formatQuantile(cpu) },
    {
      label: 'MEMORY QUANTILE',
      value: formatQuantile(memory),
    },
    { label: 'CPU OVERHEAD', value: formatOverhead(cpu?.overhead) },
    { label: 'MEMORY OVERHEAD', value: formatOverhead(memory?.overhead) },
    {
      label: 'LOOKBACK WINDOW',
      value: `CPU: ${formatLookbackWindow(
        cpu?.lookBackPeriodSeconds
      )} / Memory: ${formatLookbackWindow(memory?.lookBackPeriodSeconds)}`,
    },
    {
      label: 'MIN APPLY THRESHOLD',
      value: `CPU: ${formatApplyThreshold(
        cpu?.applyThresholdStrategy?.percentageThreshold?.percentage
      )} / Memory: ${formatApplyThreshold(
        memory?.applyThresholdStrategy?.percentageThreshold?.percentage
      )}`,
    },
    {
      label: 'WHEN TO APPLY',
      value: autoscalerModeDisplayName[resolvedApplyType] ?? resolvedApplyType,
    },
  ];
};

/** Full rationale: summary + targeting + impact (matches expanded body). */
export const getPolicyRationaleFull = (policy: WrappedPolicy): string => {
  const { summary, targetingRationale, expectedImpact } = policy.reasoning;
  return [summary, targetingRationale, expectedImpact]
    .filter(Boolean)
    .join(' ');
};

export const getApplyTimingLabel = (policy: WrappedPolicy): string => {
  const applyType =
    policy.payload.applyType ??
    policy.payload.recommendationPolicies?.applyType ??
    PolicyAutoscalerMode.UNKNOWN;
  return autoscalerModeDisplayName[applyType] ?? applyType;
};

export const getDisplayPolicies = (
  recommendation: AutopilotRecommendation
): DisplayPolicy[] => {
  const order = recommendation.policyOrder ?? [];
  const creates = (recommendation.newPolicies ?? []).map((policy) => ({
    policy,
    action: 'create' as const,
  }));
  const updates = (recommendation.updatePolicies ?? []).map((policy) => ({
    policy,
    action: 'update' as const,
  }));
  const all = [...creates, ...updates];

  if (!order.length) {
    return all;
  }

  const byName = new Map(
    all.map((entry) => [entry.policy.payload.name, entry])
  );
  const ordered = order
    .map((o) => byName.get(o.name))
    .filter((entry): entry is DisplayPolicy => !!entry);
  const unordered = all.filter(
    (entry) => !order.some((o) => o.name === entry.policy.payload.name)
  );

  return [...ordered, ...unordered];
};

export const getTotalWorkloadCount = (
  recommendation: AutopilotRecommendation | null
): number => {
  if (!recommendation) {
    return 0;
  }
  const policies = [
    ...(recommendation.newPolicies ?? []),
    ...(recommendation.updatePolicies ?? []),
  ];
  return policies.reduce(
    (sum, p) => sum + (p.targetedWorkloads?.totalCount ?? 0),
    0
  );
};

export const getPolicyCount = (
  recommendation: AutopilotRecommendation | null
): number => {
  if (!recommendation) {
    return 0;
  }
  return (
    (recommendation.newPolicies?.length ?? 0) +
    (recommendation.updatePolicies?.length ?? 0)
  );
};

export const getWorkloadRows = (policy: WrappedPolicy): WorkloadRow[] => {
  return (policy.targetedWorkloads?.items ?? []).map((item) => {
    return {
      id: item.workloadId,
      workloadId: item.workloadId,
      name: item.name,
      namespace: item.namespace,
      kind: item.kind,
    };
  });
};

type OverlapWorkload = {
  name: string;
  namespace: string;
  scalingPolicyId?: string;
  scalingPolicyName?: string;
};

export type OverlapInfo = {
  overlapCount: number;
  systemPolicyName: string;
  movingCount: number;
  policyName: string;
};

export const getOverlapInfo = (
  policy: WrappedPolicy,
  workloads: OverlapWorkload[]
): OverlapInfo | null => {
  const policyName = policy.payload.name;
  const workloadMap = new Map(
    workloads.map((w) => [`${w.namespace}/${w.name}`, w])
  );

  const overlapping = (policy.targetedWorkloads?.items ?? [])
    .map((item) => workloadMap.get(`${item.namespace}/${item.name}`))
    .filter((w): w is OverlapWorkload => !!w?.scalingPolicyId);

  if (!overlapping.length) {
    return null;
  }

  const systemPolicyName =
    overlapping[0]?.scalingPolicyName ?? 'a system policy';
  const totalCount = policy.targetedWorkloads?.totalCount ?? 0;

  return {
    overlapCount: overlapping.length,
    systemPolicyName,
    movingCount: Math.max(totalCount - overlapping.length, 0),
    policyName,
  };
};

export const getTotalNamespaceCount = (
  recommendation: AutopilotRecommendation | null
): number => {
  if (!recommendation) return 0;
  return new Set(
    [
      ...(recommendation.newPolicies ?? []),
      ...(recommendation.updatePolicies ?? []),
    ]
      .flatMap((p) => p.targetedWorkloads?.items ?? [])
      .map((i) => i.namespace)
  ).size;
};

export const getUpdatePolicyWorkloadCount = (
  recommendation: AutopilotRecommendation | null
): number => {
  if (!recommendation) {
    return 0;
  }
  return (recommendation.updatePolicies ?? []).reduce(
    (sum, p) => sum + (p.targetedWorkloads?.totalCount ?? 0),
    0
  );
};
